// @flow

import type Map from './map';
import type Route from './route';
import {loadSVGImage} from '../util/image';
import {warnOnce, extend} from '../util/util';

/**
 * Arrow options are used to define the appearance of arrows shown on the route when showArrows is set to true.
 *
 * @typedef {Object} ArrowOptions
 * @property {number} size
 * @property {number} spacing
 * @property {string} fillColor
 * @property {number} fillOpacity
 * @property {string} strokeColor
 * @property {number} strokeWidth
 * @property {number} strokeOpacity
 */
export type ArrowOptions = {
    size: number,
    spacing: number,
    fillColor: string,
    fillOpacity: number,
    strokeColor: string,
    strokeWidth: number,
    strokeOpacity: number,
};

export const defaultArrowOptions: ArrowOptions = {
    fillColor: '#FFFFFF',
    fillOpacity: 1.0,
    size: 1,
    spacing: 30,
    strokeColor: '#000000',
    strokeOpacity: 1.0,
    strokeWidth: 2,
};

export class RouteArrow {
    _route: Route;
    _map: Map;
    _name: string;
    _isVisible: boolean;
    _arrowIconName: string;
    _arrowOptions: ArrowOptions;
    _routeSourceName: string;

    constructor(route: Route) {
        this._route = route;
        this._map = this._route._map;
        this._name = `arrow-layer-${route._routeId}`;
        this._arrowIconName = `arrow-icon-${route._routeId}`;
        this._isVisible = route._isVisible;
        this._arrowOptions = defaultArrowOptions;
        this._routeSourceName = route._routeSourceName;
    }

    _loadIcon(
        iconName: string,
        iconUrl: string,
        width: number,
        height: number
    ): Promise<any> {
        return new Promise((resolve) => {
            loadSVGImage(iconUrl, width, height).then((image) => {
                if (this._map.hasImage(iconName)) {
                    this._map.removeImage(iconName);
                }
                this._map.addImage(iconName, image);
                resolve();
            });
        });
    }

    createArrowIcon(arrowOptions: ArrowOptions): string {
        return `<svg viewBox="0 0 24 20" xmlns="http://www.w3.org/2000/svg"> <polygon points="0,0 24,10 0,20 5,10" stroke-width="${arrowOptions.strokeWidth}" stroke="${arrowOptions.strokeColor}" fill="${arrowOptions.fillColor}" fill-opacity="${arrowOptions.fillOpacity}" stroke-opacity="${arrowOptions.strokeOpacity}" /> </svg>`;
    }

    getName(): string {
        return this._name;
    }

    addLayer(arrowOptions?: ArrowOptions): RouteArrow {
        // 5 cases: undefined, null, {}, partial, all options
        this._arrowOptions = extend({}, defaultArrowOptions, arrowOptions);
        const icon = this.createArrowIcon(this._arrowOptions);
        this._loadIcon(this._arrowIconName, icon, 24, 20).then(() => {
            if (this._map.getLayer(this._name)) {
                warnOnce('Arrow Layer exists');
            } else {
                const arrowLayer = {
                    id: this._name,
                    type: 'symbol',
                    source: this._routeSourceName,
                    layout: {
                        'icon-image': this._arrowIconName,
                        'icon-size': this._arrowOptions.size,
                        'icon-rotation-alignment': 'map',
                        'icon-allow-overlap': true,
                        'symbol-placement': 'line',
                        'symbol-avoid-edges': false,
                        'symbol-spacing': this._arrowOptions.spacing,
                        visibility: this._isVisible ? 'visible' : 'none',
                    },
                };
                const beforeId = this._route._stopLayerName;
                if (this._map.getLayer(beforeId)) {
                    this._map.addLayer(arrowLayer, beforeId);
                } else {
                    this._map.addLayer(arrowLayer);
                }
            }
        });
        return this;
    }

    updateLayer(arrowOptions?: ArrowOptions): RouteArrow {
        if (this._map.getLayer(this._name)) {
            // 5 cases: undefined, null, {}, partial, full update
            arrowOptions = extend({}, defaultArrowOptions, arrowOptions);
            // regenerate image if options changed
            if (
                arrowOptions.strokeColor !== this._arrowOptions.strokeColor ||
                arrowOptions.strokeOpacity !==
                    this._arrowOptions.strokeOpacity ||
                arrowOptions.strokeWidth !== this._arrowOptions.strokeWidth ||
                arrowOptions.fillColor !== this._arrowOptions.fillColor ||
                arrowOptions.fillOpacity !== this._arrowOptions.fillOpacity
            ) {
                const icon = this.createArrowIcon(arrowOptions);
                this._loadIcon(this._arrowIconName, icon, 24, 20);
            }

            // check for layer property change
            if (arrowOptions.size !== this._arrowOptions.size) {
                this._map.setLayoutProperty(
                    this._name,
                    'icon-size',
                    arrowOptions.size
                );
            }
            if (arrowOptions.spacing !== this._arrowOptions.spacing) {
                this._map.setLayoutProperty(
                    this._name,
                    'symbol-spacing',
                    arrowOptions.spacing
                );
            }
        }
        return this;
    }

    removeLayer(): RouteArrow {
        if (this._map.getLayer(this._name)) {
            this._map.removeLayer(this._name);
        }
        if (this._map.hasImage(this._arrowIconName)) {
            this._map.removeImage(this._arrowIconName);
        }
        return this;
    }

    setVisibility(isVisible: boolean): RouteArrow {
        this._isVisible = isVisible;
        const visibility = isVisible ? 'visible' : 'none';
        if (this._map.getLayer(this._name)) {
            this._map.setLayoutProperty(this._name, 'visibility', visibility);
        }
        return this;
    }
}
